<template>
  <div class="d-f f-1 ai-c jc-c" v-if="state === 'loading'">
    <div class="loading-spinner large"></div>
  </div>
  <div v-else-if="state === 'loaded'">
    <card-minimal-title title="Summary" />
    <div class="d-f f-g fd-r f-wrap f-wrap-2">
      <template v-for="(keyData, key) in totalStats">
        <KeyAdvanced v-if="keyData.show" :key="key" :label="keyData.label" :icon="keyData.icon" :total="keyData.total" :live="keyData.live" :recorded="keyData.recorded" />
      </template>
    </div>
    <template v-if="liveStats.data">
      <card-minimal-title title="Live Timeline" class="m-t-4" />
      <card-minimal class="flex f-3 graph p-b-4">
        <Graph :data="{ labels: liveStats.labels, datasets: liveStats.datasets }" :options="liveStats.options" class="m-t-2" />
        <KeyValues>
          <template v-for="(dataset, key) in liveStats.graph">
            <Key v-if="dataset.show" :key="key" :color="dataset.colorName" :icon="dataset.icon" :active="liveStats.activeDatasets.includes(key)" :total="dataset.total" :label="dataset.label" @click.native="toggleLiveStatisticsDataset(key)" />
          </template>
          <Key v-if="chatEnabled" :borderless="true" icon="chat-24" color="pink" :total="liveStats.data.users_chatting" :showPercent="liveStats.data.viewers > 0" :percent="((liveStats.data.users_chatting / liveStats.data.viewers) * 100).toFixed(1)" label="Chatting users" />
          <Key v-if="chatEnabled && liveshoppingEnabled" :borderless="true" icon="thumbs-up-24" color="green" :total="liveStats.data.users_reacting" :showPercent="liveStats.data.viewers > 0" :percent="((liveStats.data.users_reacting / liveStats.data.viewers) * 100).toFixed(1)" label="Users reacting" />
        </KeyValues>
      </card-minimal>
    </template>
    <template v-if="dayStats.data">
      <card-minimal-title title="Statistics per day" class="m-t-3" />
      <card-minimal :subtitle="`Statistics`" subtitleIcon="line-graph-24" class="flex f-3 graph">
        <template slot="subtitle-slot">
          <FormDropdown :items="timeera" @input="timeera = $event.value" class="minimal inline blue m-l-2" />
        </template>
        <Graph ref="dayGraph" :data="{ labels: dayStats.labels, datasets: dayStats.datasets }" :options="dayStats.options" />
        <KeyValues>
          <template v-for="(dataset, key) in dayStats.graph">
            <Key v-if="dataset.show" :key="`${key}-${dayStats.timeEra}`" :color="dataset.colorName" :icon="dataset.icon" :active="dayStats.activeDatasets.includes(key)" :total="dataset.total" :label="dataset.label" @click.native="toggleDayStatisticsDataset(key)" />
          </template>
        </KeyValues>
      </card-minimal>
    </template>
  </div>
  <div class="d-f f-1 ai-c jc-c" v-else>
    <div class="ta-c page-placeholder-text">
      <h2>No statistics accumulated</h2>
      <p>Statistics are available after the broadcast has completed</p>
    </div>
  </div>
</template>

<script>

import moment from 'moment';
import { AnalyticsService } from '@/services/analytics.service';

import KeyValues from '@/components/Statistics/KeyValues.vue';
import Key from '@/components/Statistics/Key.vue';
import KeyAdvanced from '@/components/Statistics/KeyAdvanced.vue';

export default {
  components: {
    KeyValues,
    Key,
    KeyAdvanced,
  },
  computed: {
    /**
     * Return the current config in store
     * @returns {Object}
     */
    lvspConfig() {
      return this.$store.getters['Play/lvspConfig'];
    },
    liveShoppingEnabledAndActive() {
      return this.activeSubscription && this.liveshoppingEnabled;
    },
    addToCartEnabled() {
      if (!this.liveShoppingEnabledAndActive) {
        return false;
      }

      const { shoppingSettings: { provider } = {} } = this;
      return this.lvspConfig?.events.includes('cart.add') || this.lvspConfig?.urls.product_add_to_cart !== 'undefined' || provider.includes('shopify');
    },
    goToProductDayAboveZero() {
      return this.dayStats.graph.go_to_product.total > 0;
    },
    goToProductLiveAboveZero() {
      return this.liveStats.graph.go_to_product.total > 0;
    },
    goToProductTotalAboveZero() {
      return this.totalStats.go_to_product.total > 0;
    },
    timeera: {
      get() {
        return [
          { value: 'from-7', string: '7 days from live' },
          { value: 'from-30', string: '30 days from live' },
          { value: 'past-7', string: 'Past 7 days' },
          { value: 'past-30', string: 'past 30 days' },
        ].map(item => ({ ...item, selected: item.value === this.dayStats.timeEra }));
      },
      async set(value) {
        const [type, days] = value.split('-');
        this.dayStats.timeEra = value;
        this.dayStats.type = type;
        this.dayStats.days = parseInt(days, 10);

        await this.getDayStatistics();
        const datasets = this.dayStats.activeDatasets;
        this.dayStats.activeDatasets = [];
        this.dayStats.labels = [];
        this.dayStats.datasets = [];
        datasets.forEach(dataset => this.toggleDayStatisticsDataset(dataset));
      },
    },
    /**
     * Return current broadcast
     * @returns {Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    chatEnabled() {
      return this.broadcast.event_details.chat.enable;
    },
  },
  methods: {
    async getTotalStatistics() {
      let data;
      try {
        data = await AnalyticsService.getBroadcastTotalStatistics(this.broadcast.id);
      } catch (e) {
        return;
      }

      // Clear and setup datasets
      Object.keys(this.totalStats).forEach((dataset) => {
        this.totalStats[dataset].data = [];
        this.totalStats[dataset].total = 0;
      });

      const activeDatasets = [];
      data.forEach((event) => {
        if (typeof this.totalStats[event.event] !== 'undefined' && event.state !== 'upcoming') {
          if (!activeDatasets.includes(event.event)) {
            activeDatasets.push(event.event);
          }

          this.totalStats[event.event].total += event.sum;
          if (event.state === 'live') {
            this.totalStats[event.event].live = event.sum;
            if (event.event === 'pageview') {
              this.liveStats.graph.viewers.total = event.sum;
            }
          } else if (event.state === 'recorded') {
            this.totalStats[event.event].recorded = event.sum;
          }
        }
      });

      Object.keys(this.totalStats).forEach((dataset) => {
        if (typeof this.totalStats[dataset].showIf !== 'undefined' ? this[this.totalStats[dataset].showIf] : true) {
          this.totalStats[dataset].show = true;
        }
      });
    },
    /**
     * Get and parse liveStatistics
     * @returns {Void}
     */
    async getLiveStatistics() {
      let data;
      try {
        data = await AnalyticsService.getBroadcastLiveStatistics(this.broadcast.id);
        this.liveStats.data = data;
      } catch (e) {
        return;
      }

      const endTime = new Date(this.broadcast.actual_end_time);
      // endTime.setMinutes(endTime.getMinutes() + 1);

      const activeDatasets = [];
      Object.keys(data.timeline).forEach((key) => {
        if (new Date(data.timeline[key].timestamp) < endTime) {
          Object.keys(data.timeline[key]).forEach((event) => {
            if (typeof this.liveStats.graph[event] !== 'undefined') {
              if (!activeDatasets.includes(event)) {
                activeDatasets.push(event);
              }
              this.liveStats.graph[event].data.push(data.timeline[key][event]);
            }
          });
        }
      });

      Object.keys(this.liveStats.graph).forEach((dataset) => {
        if (typeof this.liveStats.graph[dataset].showIf !== 'undefined' ? this[this.liveStats.graph[dataset].showIf] : true) {
          this.liveStats.graph[dataset].show = true;
        }
      });

      this.liveStats.graph.messages.total = data.messages;
      this.liveStats.graph.reactions.total = data.reactions;
      this.liveStats.graph.add_to_cart.total = this.liveStats.graph.add_to_cart.data.reduce((partialSum, a) => partialSum + a, 0);
    },
    /**
     * Get and parse day statistics
     * @returns {Void}
     */
    async getDayStatistics() {
      const {
        broadcast: {
          id,
          actual_start_time,
        },
        dayStats: {
          type,
          days,
        },
      } = this;

      const from = type === 'past' ? moment().subtract(days - 1, 'days').format('YYYY-MM-DD') : moment(actual_start_time).format('YYYY-MM-DD');
      const to = type === 'past' ? null : moment(actual_start_time).add(days - 1, 'days').format('YYYY-MM-DD');
      const data = await AnalyticsService.getBroadcastStatistics(id, from, to);
      this.dayStats.data = data;

      const sortedDatasets = [];

      // Clear all datasets data
      Object.keys(this.dayStats.graph).forEach((dataset) => {
        this.dayStats.graph[dataset].data = [];
        this.dayStats.graph[dataset].total = 0;
      });

      data.forEach((event) => {
        if (typeof this.dayStats.graph[event.event] !== 'undefined' && event.state !== 'upcoming') {
          if (!sortedDatasets.includes(event.event)) {
            sortedDatasets.push(event.event);
          }
          this.dayStats.graph[event.event].data = this.dayStats.graph[event.event].data.concat(event.dataset);
        }
      });

      sortedDatasets.forEach((dataset) => {
        // Clear datasets total
        this.dayStats.graph[dataset].total = 0;
        const datasetData = {};
        let total = 0;
        this.dayStats.graph[dataset].data.forEach((entry) => {
          if (typeof datasetData[entry.date] === 'undefined') {
            datasetData[entry.date] = 0;
          }
          datasetData[entry.date] += entry.value;
          total += entry.value;
        });

        this.dayStats.graph[dataset].total = total;
        this.dayStats.graph[dataset].data = Object.keys(datasetData).map(key => ({ date: key, value: datasetData[key] }));
      });

      Object.keys(this.dayStats.graph).forEach((dataset) => {
        if (typeof this.dayStats.graph[dataset].showIf !== 'undefined' ? this[this.dayStats.graph[dataset].showIf] : true) {
          this.dayStats.graph[dataset].show = true;
        }
      });
    },
    /**
     * Toggle liveStatistics datasets
     * @returns {Void}
     */
    toggleLiveStatisticsDataset(dataset) {
      const index = this.liveStats.datasets.findIndex(d => d.label === this.liveStats.graph[dataset].label);
      if (index >= 0) {
        if (this.liveStats.datasets.length === 1) {
          return;
        }

        this.liveStats.datasets.splice(index, 1);
        this.liveStats.activeDatasets.splice(this.liveStats.activeDatasets.indexOf(dataset), 1);
        return;
      }

      const [first] = Object.keys(this.liveStats.graph);
      this.liveStats.labels = new Array(this.liveStats.graph[first].data.length).fill(0).map((_, i) => i);

      const data = [];
      this.liveStats.graph[dataset].data.forEach(entry => data.push(entry));

      this.liveStats.datasets.push({
        backgroundColor: this.liveStats.graph[dataset].color,
        borderColor: this.liveStats.graph[dataset].color,
        label: this.liveStats.graph[dataset].label,
        data,
        tension: 0.2,
        beginAtZero: true,
        pointRadius: 0,
        pointHitRadius: 20,
      });

      this.liveStats.activeDatasets.push(dataset);
    },
    /**
     * Toggle dayStatistics datasets
     * @returns {Void}
     */
    toggleDayStatisticsDataset(dataset) {
      const {
        broadcast: {
          actual_start_time,
        },
        dayStats: {
          type,
        },
      } = this;

      let {
        dayStats: {
          days,
        },
      } = this;

      const index = this.dayStats.datasets.findIndex(d => d.label === this.dayStats.graph[dataset].label);
      if (index >= 0) {
        if (this.dayStats.datasets.length === 1) {
          return;
        }

        this.dayStats.datasets.splice(index, 1);
        this.dayStats.activeDatasets.splice(this.dayStats.activeDatasets.indexOf(dataset), 1);
        return;
      }

      // Fill up the labels
      const date = type === 'past' ? moment() : moment(actual_start_time);
      const labels = [date.format('DD/M')];
      let n = days - 1;
      while (n !== 0) {
        if (type === 'past') {
          date.subtract(1, 'day');
          if (date.isBefore(moment(actual_start_time))) {
            days -= n;
            break;
          }
        } else {
          date.add(1, 'day');
          if (date.isAfter(moment())) {
            days -= n;
            break;
          }
        }
        labels.push(date.format('DD/M'));
        n -= 1;
      }

      this.dayStats.labels = type === 'past' ? labels.reverse() : labels;

      // Add some data
      const data = new Array(days).fill(0);
      this.dayStats.graph[dataset].data.forEach((entry) => {
        const diff = type === 'past' ? Math.floor(moment().diff(new Date(entry.date), 'hours') / 24) : Math.abs(Math.floor(moment(actual_start_time).diff(new Date(entry.date), 'hours') / 24));
        data[diff] += entry.value;
      });

      this.dayStats.datasets.push({
        backgroundColor: this.dayStats.graph[dataset].color,
        borderColor: this.dayStats.graph[dataset].color,
        label: this.dayStats.graph[dataset].label,
        data: type === 'past' ? data.reverse() : data,
        tension: 0.2,
        beginAtZero: true,
        pointRadius: 0,
        pointHitRadius: 20,
      });

      this.dayStats.activeDatasets.push(dataset);
    },
  },
  data() {
    return {
      state: 'loading',
      bajskey: 0,
      totalStats: {
        pageview: {
          icon: 'eye-24',
          label: 'Viewers',
          show: true,
          total: 0,
          live: 0,
          recorded: 0,
        },
        add_to_cart: {
          icon: 'shoppingbag-24',
          label: 'Add to cart',
          show: false,
          showIf: 'addToCartEnabled',
          total: 0,
          live: 0,
          recorded: 0,
        },
        go_to_product: {
          icon: 'shoppingbag-24',
          label: 'Go to product',
          show: false,
          showIf: 'goToProductTotalAboveZero',
          total: 0,
          live: 0,
          recorded: 0,
        },
        view_product: {
          icon: 'shoppingbag-24',
          label: 'View product',
          show: false,
          showIf: 'liveShoppingEnabledAndActive',
          total: 0,
          live: 0,
          recorded: 0,
        },
        jump_to_product: {
          icon: 'shoppingbag-24',
          label: 'Jump to product',
          show: false,
          showIf: 'liveShoppingEnabledAndActive',
          total: 0,
          live: 0,
          recorded: 0,
        },
      },
      dayStats: {
        data: null,
        type: 'from',
        days: 7,
        timeEra: 'from-7',
        options: {
          scales: {
            y: {
              ticks: {
                display: true,
              },
            },
          },
        },
        graph: {
          pageview: {
            color: '#007FFF',
            colorName: 'blue',
            icon: 'eye-24',
            label: 'Viewers',
            show: true,
            total: 0,
            data: [],
          },
          add_to_cart: {
            color: '#00B5D6',
            colorName: 'ocean',
            icon: 'shoppingbag-24',
            label: 'Add to cart',
            show: false,
            showIf: 'addToCartEnabled',
            total: 0,
            data: [],
          },
          go_to_product: {
            color: '#00B5D6',
            colorName: 'ocean',
            icon: 'shoppingbag-24',
            label: 'Go to product',
            show: false,
            showIf: 'goToProductDayAboveZero',
            total: 0,
            data: [],
          },
          view_product: {
            color: '#00CF4D',
            colorName: 'green',
            icon: 'shoppingbag-24',
            label: 'View product',
            show: false,
            showIf: 'liveShoppingEnabledAndActive',
            total: 0,
            data: [],
          },
          jump_to_product: {
            color: '#FF4C4C',
            colorName: 'peach',
            icon: 'shoppingbag-24',
            label: 'Jump to product',
            show: false,
            showIf: 'liveShoppingEnabledAndActive',
            total: 0,
            data: [],
          },
        },
        labels: [],
        datasets: [],
        activeDatasets: [],
      },
      liveStats: {
        data: null,
        options: {
          scales: {
            y: {
              ticks: {
                display: true,
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                title: context => `${context[0].label} minute${parseInt(context[0].label, 10) === 1 ? '' : 's'} in`,
              },
            },
          },
        },
        graph: {
          viewers: {
            color: '#007FFF',
            colorName: 'blue',
            icon: 'eye-24',
            show: false,
            label: 'Viewers',
            total: 0,
            data: [],
          },
          messages: {
            color: '#6500F0',
            colorName: 'purple',
            icon: 'chat-24',
            show: false,
            showIf: 'chatEnabled',
            label: 'Messages',
            total: 0,
            data: [],
          },
          reactions: {
            color: '#FF7A00',
            colorName: 'orange',
            icon: 'thumbs-up-24',
            show: false,
            showIf: 'liveShoppingEnabledAndActive',
            label: 'Reactions',
            total: 0,
            data: [],
          },
          add_to_cart: {
            color: '#00B5D6',
            colorName: 'ocean',
            icon: 'shoppingbag-24',
            show: false,
            showIf: 'addToCartEnabled',
            label: 'Add to cart',
            total: 0,
            data: [],
          },
          go_to_product: {
            color: '#00B5D6',
            colorName: 'ocean',
            icon: 'shoppingbag-24',
            show: false,
            showIf: 'goToProductLiveAboveZero',
            label: 'Go to product',
            total: 0,
            data: [],
          },
        },
        labels: [],
        datasets: [],
        activeDatasets: [],
      },
    };
  },
  async mounted() {
    if (!['complete'].includes(this.broadcast.status.life_cycle_status)) {
      this.state = 'nostats';
      return;
    }

    await this.getTotalStatistics();
    await this.getLiveStatistics();
    await this.getDayStatistics();

    this.toggleLiveStatisticsDataset('viewers');
    this.toggleDayStatisticsDataset('pageview');

    this.state = 'loaded';
  },
};
</script>
